import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useApiClient, useQueryParams } from '@marageti/z4-lib';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import LoginLayout from '../../login/login-layout';
import { buildLoginPath } from '../../login/helpers';
import { SendLinkView, SubmittedView } from './send-link-view';

const RESET_PATH = '/reset';
const RESET_SEND_EMAIL_PATH = '/reset/send-email';

type ResetLocationState = {
  alreadySent?: boolean;
};

const buildResetPath = (path: string, searchParams: URLSearchParams): string => {
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
};

const SendLinkContainer = () => {
  const apiClient = useApiClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { email: emailQueryParam } = useQueryParams();
  const locationState = (location.state || {}) as ResetLocationState;
  const normalizedPath = location.pathname.replace(/\/+$/, '') || RESET_PATH;
  const isSendEmailStep = normalizedPath === RESET_SEND_EMAIL_PATH;
  const isFormStep = normalizedPath === RESET_PATH;

  const [email, setEmail] = useState(emailQueryParam || '');
  const [isSending, setIsSending] = useState(false);
  const autoSentRef = useRef(false);

  const sendResetLink = useCallback(async (targetEmail: string) => {
    const queryParams: Record<string, string> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of search.entries()) {
      queryParams[key] = value;
    }

    // Always treat as success so we do not leak whether the email exists.
    await apiClient.authClient.postUserReset(targetEmail, { params: queryParams })
      .catch(console.warn);
  }, [apiClient.authClient, search]);

  const goToSendEmail = (nextEmail: string, alreadySent = false) => {
    const params = new URLSearchParams(search);
    params.set('email', nextEmail);
    setEmail(nextEmail);
    navigate(buildResetPath(RESET_SEND_EMAIL_PATH, params), {
      replace: true,
      state: { alreadySent },
    });
  };

  const goToLogin = () => {
    const params = new URLSearchParams(search);
    const loginEmail = email || emailQueryParam;
    if (loginEmail) {
      params.set('email', loginEmail);
    } else {
      params.delete('email');
    }
    navigate(buildLoginPath('user_identification', params), { replace: true });
  };

  useEffect(() => {
    if (!isSendEmailStep || !emailQueryParam || autoSentRef.current) {
      return undefined;
    }
    if (locationState.alreadySent) {
      autoSentRef.current = true;
      setEmail(emailQueryParam);
      return undefined;
    }

    autoSentRef.current = true;
    let cancelled = false;

    setIsSending(true);
    sendResetLink(emailQueryParam)
      .then(() => {
        if (!cancelled) {
          setEmail(emailQueryParam);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSending(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [emailQueryParam, isSendEmailStep, locationState.alreadySent, sendResetLink]);

  const handleSubmit = async (nextEmail: string) => {
    setIsSending(true);
    try {
      await sendResetLink(nextEmail);
      goToSendEmail(nextEmail, true);
    } finally {
      setIsSending(false);
    }
  };

  const handleResendLink = async () => {
    if (isSending || !email) {
      return;
    }
    setIsSending(true);
    try {
      await sendResetLink(email);
    } finally {
      setIsSending(false);
    }
  };

  // Email already known — skip the form; never land on /reset?email=...
  if (isFormStep && emailQueryParam) {
    return (
      <Navigate
        to={buildResetPath(RESET_SEND_EMAIL_PATH, search)}
        replace
      />
    );
  }

  if (isSendEmailStep && !(email || emailQueryParam)) {
    return (
      <Navigate
        to={buildLoginPath('user_identification', new URLSearchParams(search))}
        replace
      />
    );
  }

  const handleCallSupport = () => {
    window.open('tel:8884412418', '_blank');
  };

  return (
    <LoginLayout>
      {isSendEmailStep ? (
        <SubmittedView
          email={email || emailQueryParam}
          isSending={isSending}
          onBack={goToLogin}
          onResendLink={handleResendLink}
          onCallSupport={handleCallSupport}
        />
      ) : (
        <SendLinkView
          defaultEmail={email || emailQueryParam || ''}
          isSubmitting={isSending}
          onSubmit={handleSubmit}
        />
      )}
    </LoginLayout>
  );
};

export default SendLinkContainer;
